import { Divider, Paper, Stack, styled, Typography } from "@mui/material";
import {
  C_CHART_6,
  C_CHART_7,
  C_CHART_8,
  C_ON_SURFACE,
  C_ON_SURFACE_LOW_EMPHASIS,
  C_ON_SURFACE_VARIANT,
  C_PRIMARY,
  C_SURFACE,
} from "~/common/components/icon/Colors";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { useTranslation } from "react-i18next";
import { CURRENCY_ISO } from "~/utils/constants";
import { formatCurrency } from "~/utils/currencyFormatter";
interface ChartTooltipProps {
  data: {
    invoice: number | null;
    orderbook: number | null;
    target: number | null;
    gap: number | null;
  };
}

export function ChartTooltip({ data }: ChartTooltipProps) {
  const { t } = useTranslation();

  const { invoice, target, orderbook, gap } = data;

  const isLastFiveYearGraph = !target && !gap;

  const rowContent = (title: string, value: number, color: string) => {
    return (
      <Row>
        <RowTitle>
          <Dot color={color} />
          <Typography color={C_ON_SURFACE_LOW_EMPHASIS}>{title}</Typography>
        </RowTitle>
        <Typography component={"b"} color={C_ON_SURFACE_VARIANT}>
          {formatCurrency(value, CURRENCY_ISO)}
        </Typography>
      </Row>
    );
  };

  const renderInvoice = (
    title = isLastFiveYearGraph ? t("cpd.graph2.hoverTooltip.invoice") : t("cpd.graph1.hoverTooltip.invoice"),
  ) => {
    if (!invoice) {
      return null;
    }
    return rowContent(title, invoice, C_CHART_6);
  };

  const renderOrderbook = (
    title = isLastFiveYearGraph ? t("cpd.graph2.hoverTooltip.orderbook") : t("cpd.graph1.hoverTooltip.orderbook"),
  ) => {
    if (!orderbook) {
      return null;
    }
    return rowContent(title, orderbook, C_CHART_7);
  };

  const renderGap = (title = t("cpd.graph1.hoverTooltip.gap")) => {
    if (!gap) {
      return null;
    }
    return rowContent(title, gap, "transparent");
  };

  const renderTarget = (targetAchieved = false, title = t("cpd.graph1.hoverTooltip.target")) => {
    if (!target) {
      return null;
    }
    return (
      <Row>
        <RowTitle>
          {targetAchieved ? <StyledCheckIcon /> : <Dot color={C_CHART_8} />}
          <Typography color={C_ON_SURFACE_LOW_EMPHASIS}>{title}</Typography>
        </RowTitle>
        <Typography component={"b"} color={C_ON_SURFACE_VARIANT}>
          {formatCurrency(target, CURRENCY_ISO)}
        </Typography>
      </Row>
    );
  };

  if (isLastFiveYearGraph) {
    return (
      <TooltipView>
        <Stack>
          {renderInvoice()}
          {renderOrderbook()}
        </Stack>
      </TooltipView>
    );
  }

  if ((invoice ?? 0) >= (target ?? 0)) {
    return (
      <TooltipView>
        <Stack>
          {renderInvoice()}
          {renderTarget(true)}
        </Stack>
      </TooltipView>
    );
  }

  if (orderbook == null) {
    return (
      <TooltipView>
        <Stack>
          {renderInvoice()}
          {renderTarget()}
          {renderGap()}
        </Stack>
      </TooltipView>
    );
  }

  if (invoice == null) {
    return (
      <TooltipView>
        <Stack>
          {renderOrderbook()}
          {renderTarget()}
          {renderGap()}
        </Stack>
      </TooltipView>
    );
  }
  return (
    <TooltipView>
      <Typography variant="bodySmall" color={C_ON_SURFACE_LOW_EMPHASIS}>
        {t("cpd.graph1.hoverTooltip.total")}
      </Typography>
      <Typography variant="titleLarge" color={C_ON_SURFACE_VARIANT}>
        {formatCurrency(invoice + orderbook, CURRENCY_ISO)}
      </Typography>
      <Stack>
        {renderInvoice()}
        {renderOrderbook()}
        <StyledDivider />
        {renderTarget()}
        {renderGap()}
      </Stack>
    </TooltipView>
  );
}

const TooltipView = styled(Paper)({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  padding: "8px",
  backgroundColor: C_SURFACE,
  boxShadow: "none",
  borderRadius: "8px",
  color: C_ON_SURFACE,
});

const Row = styled(Stack)({
  flexDirection: "row",
  gap: "8px",
  justifyContent: "space-between",
});

const Dot = styled("div")(({ color }) => ({
  width: "8px",
  height: "8px",
  borderRadius: "4px",
  backgroundColor: color || C_CHART_6,
}));

const CheckIcon = styled(TaskAltIcon)(({ color }) => ({
  fontSize: "12px",
  marginRight: "4px",
  color: color || "transparent",
}));

const RowTitle = styled(Stack)({
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: "4px",
});

const StyledCheckIcon = styled(CheckIcon)({
  fontSize: "12px",
  color: C_PRIMARY,
});

const StyledDivider = styled(Divider)({
  margin: "4px 0px",
});
